import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './config';
import { Hotel, HotelWithProgress, Task, TaskComment, TaskFile, TaskTemplate, Category, User, Feedback, FeedbackComment } from '../types';
import { convertFirestoreDoc, convertToFirestore } from './utils';
import { logger } from '../utils/logger';

// Hotels
export const hotelsCollection = collection(db, 'hotels');

// Feedback
export const feedbackCollection = collection(db, 'feedback');

// Real-time listeners
export const subscribeToHotels = (callback: (hotels: HotelWithProgress[]) => void) => {
  return onSnapshot(hotelsCollection, async (querySnapshot) => {
    try {
      // Get all hotels first
      const hotels: HotelWithProgress[] = querySnapshot.docs.map(docSnapshot => {
      const hotelData = docSnapshot.data() as Hotel;
        return {
        ...hotelData,
        id: docSnapshot.id,
        totalTasks: 0,
        completedTasks: 0,
        progressPercentage: 0
        } as HotelWithProgress;
      });

      // Calculate progress for each hotel in parallel
      const hotelsWithProgress = await Promise.all(
        hotels.map(async (hotel) => {
          try {
            const tasks = await getHotelTasks(hotel.id);
            const totalTasks = tasks.length;
            const completedTasks = tasks.filter(task => task.status === 'completed').length;
            const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
            
            return {
              ...hotel,
              totalTasks,
              completedTasks,
              progressPercentage
            };
          } catch (error) {
            logger.error(`Error calculating progress for hotel ${hotel.id}:`, error);
            return hotel; // Return hotel with zero progress if calculation fails
          }
        })
      );
      
      callback(hotelsWithProgress);
    } catch (error) {
      logger.error('Error in subscribeToHotels:', error);
      callback([]);
    }
  });
};

export const subscribeToHotel = (hotelId: string, callback: (hotel: HotelWithProgress | null) => void) => {
  const docRef = doc(db, 'hotels', hotelId);
  
  return onSnapshot(docRef, 
    (docSnap) => {
      try {
        if (!docSnap.exists()) {
          callback(null);
          return;
        }
        
        const hotelData = docSnap.data() as Hotel;
        
        // Validate hotel data structure
        if (!hotelData.name || !hotelData.contactPerson) {
          logger.warn(`Invalid hotel data for ${docSnap.id}:`, hotelData);
          callback(null);
          return;
        }
        
        const hotelWithProgress: HotelWithProgress = {
          ...hotelData,
          id: docSnap.id,
          totalTasks: 0, // Will be updated by subscribeToHotelProgress
          completedTasks: 0, // Will be updated by subscribeToHotelProgress
          progressPercentage: 0 // Will be updated by subscribeToHotelProgress
        };
        
        callback(hotelWithProgress);
      } catch (error) {
        logger.error(`Error processing hotel data for ${hotelId}:`, error);
        callback(null);
      }
    },
    (error) => {
      logger.error(`Error in hotel listener for ${hotelId}:`, error);
      callback(null);
    }
  );
};

// Separate efficient progress calculation listener
export const subscribeToHotelProgress = (hotelId: string, callback: (progress: { totalTasks: number; completedTasks: number; progressPercentage: number }) => void) => {
  const tasksCollection = collection(db, 'tasks');
  const q = query(tasksCollection, where('hotelId', '==', hotelId));
  
  return onSnapshot(q, (querySnapshot) => {
    try {
      const tasks = querySnapshot.docs.map(doc => doc.data());
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter(task => task.status === 'completed').length;
      const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      
      callback({ totalTasks, completedTasks, progressPercentage });
    } catch (error) {
      logger.error(`Error calculating progress for hotel ${hotelId}:`, error);
      callback({ totalTasks: 0, completedTasks: 0, progressPercentage: 0 });
    }
  });
};

export const subscribeToHotelTasks = (hotelId: string, callback: (tasks: Task[]) => void) => {
  const tasksCollection = collection(db, 'tasks');
  const q = query(tasksCollection, where('hotelId', '==', hotelId));
  
  // Retry mechanism
  let retryCount = 0;
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  
  // Track all active listeners for proper cleanup
  let activeListeners: Map<string, (() => void)[]> = new Map();
  let isUnsubscribed = false;
  
  // Maintain current state of tasks with proper closure management
  let currentTasksRef = { current: [] as Task[] };
  
  // Debounce callback to prevent excessive updates
  let debounceTimeout: NodeJS.Timeout | null = null;
  const debouncedCallback = (tasks: Task[]) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout(() => {
      if (!isUnsubscribed) {
        currentTasksRef.current = tasks; // Update current state
        callback(tasks);
      }
      debounceTimeout = null; // Clear reference to prevent memory leaks
    }, 100); // 100ms debounce
  };
  
  // Cleanup function for all listeners
  const cleanupListeners = () => {
    activeListeners.forEach((listeners) => {
      listeners.forEach(unsubscribe => unsubscribe());
    });
    activeListeners.clear();
  };
  
  // Atomic state update function to prevent race conditions
  const updateTaskState = (taskId: string, updates: Partial<Task>) => {
    if (isUnsubscribed) return;
    
    currentTasksRef.current = currentTasksRef.current.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    );
    
    // Sort tasks after update
    const sortedTasks = currentTasksRef.current.sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return (a.order || 0) - (b.order || 0);
    });
    
    debouncedCallback(sortedTasks);
  };
  
  // Main tasks listener with proper error handling
  const tasksUnsubscribe = onSnapshot(q, 
    async (querySnapshot) => {
      if (isUnsubscribed) return;
      
      try {
        retryCount = 0; // Reset retry count on successful operation
        
        const tasks = querySnapshot.docs.map(doc => {
          const data = doc.data();
          // Validate task data structure
          if (!data.hotelId || !data.title || !data.status) {
            logger.warn(`Invalid task data for ${doc.id}:`, data);
            return null;
          }
          
          return {
            ...data,
            id: doc.id,
            files: [] as TaskFile[],
            comments: [] as TaskComment[]
          } as Task;
        }).filter(Boolean) as Task[];
        
        // Clean up old listeners for tasks that no longer exist
        const currentTaskIds = new Set(tasks.map(task => task.id));
        const oldTaskIds = new Set(activeListeners.keys());
        
        oldTaskIds.forEach(taskId => {
          if (!currentTaskIds.has(taskId)) {
            const listeners = activeListeners.get(taskId);
            if (listeners) {
              listeners.forEach(unsubscribe => unsubscribe());
              activeListeners.delete(taskId);
            }
          }
        });
        
        // Sort tasks by category and order
        const sortedTasks = tasks.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return (a.order || 0) - (b.order || 0);
        });
        
        // Update current tasks state while preserving existing subcollection data
        currentTasksRef.current = currentTasksRef.current.map(existingTask => {
          const updatedTask = sortedTasks.find(task => task.id === existingTask.id);
          if (updatedTask) {
            // Preserve existing comments and files, only update other properties
            return {
              ...updatedTask,
              comments: existingTask.comments || [],
              files: existingTask.files || []
            };
          }
          return existingTask;
        });
        
        // Add new tasks that don't exist in current state
        sortedTasks.forEach(newTask => {
          const exists = currentTasksRef.current.some(task => task.id === newTask.id);
          if (!exists) {
            currentTasksRef.current.push({
              ...newTask,
              comments: [],
              files: []
            });
          }
        });
        
        // Remove tasks that no longer exist
        currentTasksRef.current = currentTasksRef.current.filter(task => 
          sortedTasks.some(sortedTask => sortedTask.id === task.id)
        );
        
        // For all updates, use the preserved data and ensure proper sorting
        const sortedCurrentTasks = currentTasksRef.current.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return (a.order || 0) - (b.order || 0);
        });
        debouncedCallback(sortedCurrentTasks);
        
        // Set up real-time listeners for new tasks ONLY
        sortedTasks.forEach((task) => {
          if (!activeListeners.has(task.id)) {
            const listeners: (() => void)[] = [];
            
            // Comments listener with error handling
            const commentsCollection = collection(db, 'tasks', task.id, 'comments');
            const commentsUnsubscribe = onSnapshot(commentsCollection, 
              (commentsSnapshot) => {
                if (isUnsubscribed) return;
                
                try {
                  const comments = commentsSnapshot.docs.map(commentDoc => convertFirestoreDoc<TaskComment>(commentDoc));
                  // Sort comments by timestamp (oldest first)
                  const sortedComments = comments.sort((a, b) => {
                    const dateA = new Date(a.timestamp).getTime();
                    const dateB = new Date(b.timestamp).getTime();
                    return dateA - dateB;
                  });
                  
                  updateTaskState(task.id, { comments: sortedComments });
                } catch (error) {
                  logger.error(`Error processing comments for task ${task.id}:`, error);
                }
              },
              (error) => {
                logger.error(`Error in comments listener for task ${task.id}:`, error);
              }
            );
            listeners.push(commentsUnsubscribe);
            
            // Files listener with error handling
            const filesCollection = collection(db, 'tasks', task.id, 'files');
            const filesUnsubscribe = onSnapshot(filesCollection, 
              (filesSnapshot) => {
                if (isUnsubscribed) return;
                
                try {
                  const files = filesSnapshot.docs.map(fileDoc => convertFirestoreDoc<TaskFile>(fileDoc));
                  updateTaskState(task.id, { files });
                } catch (error) {
                  logger.error(`Error processing files for task ${task.id}:`, error);
                }
              },
              (error) => {
                logger.error(`Error in files listener for task ${task.id}:`, error);
              }
            );
            listeners.push(filesUnsubscribe);
            
            activeListeners.set(task.id, listeners);
          }
        });
        
      } catch (error) {
        logger.error(`Error in main tasks listener:`, error);
        
        // Retry mechanism for recoverable errors - fixed to prevent infinite loops
        if (retryCount < maxRetries && !isUnsubscribed) {
          retryCount++;
          logger.warn(`Retrying tasks listener (${retryCount}/${maxRetries}) in ${retryDelay}ms...`);
          // Don't call tasksUnsubscribe() - let the error handler manage retries
          // The listener will automatically retry on the next successful operation
        } else if (retryCount >= maxRetries) {
          logger.error(`Max retries exceeded for tasks listener. Stopping retries.`);
          callback([]); // Return empty array to prevent UI freeze
        }
      }
    },
    (error) => {
      logger.error(`Error in tasks query listener:`, error);
      
      // Retry mechanism for listener errors - fixed to prevent infinite loops
      if (retryCount < maxRetries && !isUnsubscribed) {
        retryCount++;
        logger.warn(`Retrying tasks query listener (${retryCount}/${maxRetries}) in ${retryDelay}ms...`);
        // Don't call tasksUnsubscribe() - let the error handler manage retries
        // The listener will automatically retry on the next successful operation
      } else if (retryCount >= maxRetries) {
        logger.error(`Max retries exceeded for tasks query listener. Stopping retries.`);
        callback([]); // Return empty array to prevent UI freeze
      }
    }
  );
  
  // Return cleanup function
  return () => {
    isUnsubscribed = true;
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }
    cleanupListeners();
    tasksUnsubscribe();
  };
};

// Subscribe to comments for a specific task
export const subscribeToTaskComments = (taskId: string, callback: (comments: TaskComment[]) => void) => {
  const commentsCollection = collection(db, 'tasks', taskId, 'comments');
  
  return onSnapshot(commentsCollection, 
    (querySnapshot) => {
      try {
        const comments = querySnapshot.docs.map(commentDoc => convertFirestoreDoc<TaskComment>(commentDoc));
        // Sort comments by timestamp (oldest first)
        const sortedComments = comments.sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return dateA - dateB;
        });
        callback(sortedComments);
      } catch (error) {
        logger.error(`Error processing comments for task ${taskId}:`, error);
        callback([]); // Return empty array on error
      }
    },
    (error) => {
      logger.error(`Error in comments listener for task ${taskId}:`, error);
      callback([]); // Return empty array on error
    }
  );
};

// Note: loadTaskWithSubcollections function removed to prevent expensive database reads
// Real-time listeners now handle subcollection data loading efficiently

export const getHotels = async (): Promise<HotelWithProgress[]> => {
  try {
    const querySnapshot = await getDocs(hotelsCollection);
    
    // Calculate progress for all hotels in parallel
    const hotelsWithProgress = await Promise.all(
      querySnapshot.docs.map(async (docSnapshot) => {
        const hotelData = docSnapshot.data() as Hotel;
        
        // Get tasks for this hotel to calculate progress
      const tasks = await getHotelTasks(docSnapshot.id);
        const totalTasks = tasks.length;
        const completedTasks = tasks.filter(task => task.status === 'completed').length;
        const progressPercentage = totalTasks > 0 
          ? Math.round((completedTasks / totalTasks) * 100)
          : 0;
        
        return {
          ...hotelData,
          id: docSnapshot.id,
          totalTasks,
          completedTasks,
          progressPercentage
        } as HotelWithProgress;
      })
    );
    
    return hotelsWithProgress;
  } catch (error) {
    logger.error('Error getting hotels:', error);
    throw error;
  }
};

export const getHotel = async (id: string): Promise<HotelWithProgress> => {
  try {
    const docRef = doc(db, 'hotels', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Hotel not found');
    }
    
    const hotelData = docSnap.data() as Hotel;
    
    // Get tasks for this hotel to calculate progress
    const tasks = await getHotelTasks(id);
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    const progressPercentage = totalTasks > 0 
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;
    
    const hotelWithProgress: HotelWithProgress = {
      ...hotelData,
      id: docSnap.id,
      totalTasks,
      completedTasks,
      progressPercentage
    };
    
    return hotelWithProgress;
  } catch (error) {
    logger.error('Error getting hotel:', error);
    throw error;
  }
};

export const createHotel = async (hotelData: Omit<Hotel, 'id'>): Promise<HotelWithProgress> => {
  try {
    const docRef = await addDoc(hotelsCollection, {
      ...hotelData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      ...hotelData,
      id: docRef.id,
      totalTasks: 0,
      completedTasks: 0,
      progressPercentage: 0
    };
  } catch (error) {
    console.error('Error creating hotel:', error);
    throw error;
  }
};

export const updateHotel = async (id: string, updates: Partial<Hotel>): Promise<HotelWithProgress> => {
  try {
    const docRef = doc(db, 'hotels', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return await getHotel(id);
  } catch (error) {
    console.error('Error updating hotel:', error);
    throw error;
  }
};

export const deleteHotel = async (id: string): Promise<void> => {
  try {
    logger.log(`🗑️ Deleting hotel: ${id}`);
    
    // First, get all tasks for this hotel (optimized version)
    const tasks = await getHotelTasks(id);
    logger.log(`📋 Found ${tasks.length} tasks to delete for hotel ${id}`);
    
    // Delete all tasks for this hotel
    for (const task of tasks) {
      logger.log(`🗑️ Deleting task: ${task.id} (${task.title})`);
      await deleteTask(task.id);
    }
    
    // Delete the hotel document
    const docRef = doc(db, 'hotels', id);
    await deleteDoc(docRef);
    
    logger.log(`✅ Successfully deleted hotel ${id} and ${tasks.length} associated tasks`);
  } catch (error) {
    logger.error('Error deleting hotel:', error);
    throw error;
  }
};

// Tasks - Optimized version without subcollection loading
export const getHotelTasks = async (hotelId: string): Promise<Task[]> => {
  try {
    logger.log(`🔥 Firestore: Querying tasks for hotel ${hotelId}`);
    const tasksCollection = collection(db, 'tasks');
    const q = query(tasksCollection, where('hotelId', '==', hotelId));
    const querySnapshot = await getDocs(q);
    
    const tasks = querySnapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      files: [] as TaskFile[], // Initialize empty - loaded by real-time listeners
      comments: [] as TaskComment[] // Initialize empty - loaded by real-time listeners
    })) as Task[];
    
    // Sort tasks by order field within each category
    const sortedTasks = tasks.sort((a, b) => {
      // First sort by category order (if available)
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      // Then sort by task order within category
      return (a.order || 0) - (b.order || 0);
    });
    
    logger.log(`🔥 Firestore: Found ${sortedTasks.length} tasks for hotel ${hotelId} (subcollections loaded by real-time listeners)`);
    return sortedTasks;
  } catch (error) {
    logger.error('Error getting hotel tasks:', error);
    throw error;
  }
};

export const createTask = async (taskData: Omit<Task, 'id'>): Promise<Task> => {
  try {
    const tasksCollection = collection(db, 'tasks');
    
    // If no order is provided, assign a default order (can be optimized later)
    let finalTaskData = taskData;
    if (taskData.order === undefined) {
      // Use timestamp-based ordering to avoid expensive queries
      finalTaskData = {
        ...taskData,
        order: Date.now() // Use timestamp as order for uniqueness
      };
      console.log(`📝 Assigning timestamp-based order ${finalTaskData.order} to new task in category ${taskData.category}`);
    }
    
    const docRef = await addDoc(tasksCollection, {
      ...finalTaskData,
      comments: [],
      files: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      ...finalTaskData,
      comments: [],
      files: [],
      id: docRef.id
    };
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

export const updateTask = async (id: string, updates: Partial<Task>): Promise<Task> => {
  try {
    const docRef = doc(db, 'tasks', id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    const docSnap = await getDoc(docRef);
    return {
      ...docSnap.data(),
      id: docSnap.id
    } as Task;
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

export const deleteTask = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'tasks', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// Task Comments
export const addTaskComment = async (taskId: string, comment: Omit<TaskComment, 'id'>): Promise<TaskComment> => {
  try {
    const commentsCollection = collection(db, 'tasks', taskId, 'comments');
    const docRef = await addDoc(commentsCollection, {
      ...comment,
      timestamp: serverTimestamp()
    });
    
    return {
      ...comment,
      id: docRef.id
    };
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const deleteTaskComment = async (taskId: string, commentId: string): Promise<void> => {
  try {
    const commentRef = doc(db, 'tasks', taskId, 'comments', commentId);
    await deleteDoc(commentRef);
    
    logger.log(`🗑️ Deleted comment ${commentId} from task ${taskId}`);
  } catch (error) {
    logger.error(`Error deleting comment ${commentId} from task ${taskId}:`, error);
    throw error;
  }
};

// Task Files
export const addTaskFile = async (taskId: string, file: Omit<TaskFile, 'id'>): Promise<TaskFile> => {
  try {
    const filesCollection = collection(db, 'tasks', taskId, 'files');
    const docRef = await addDoc(filesCollection, {
      ...file,
      addedAt: serverTimestamp()
    });
    
    return {
      ...file,
      id: docRef.id
    };
  } catch (error) {
    console.error('Error adding file:', error);
    throw error;
  }
};

export const deleteTaskFile = async (taskId: string, fileId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'tasks', taskId, 'files', fileId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Template Collection Functions
export const templatesCollection = collection(db, 'templates');

export const getTemplates = async (): Promise<TaskTemplate[]> => {
  try {
    const querySnapshot = await getDocs(templatesCollection);
    const templates = querySnapshot.docs.map(doc => convertFirestoreDoc<TaskTemplate>(doc));
    
    // Sort by order field
    return templates.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting templates:', error);
    throw error;
  }
};

export const getTemplate = async (id: string): Promise<TaskTemplate> => {
  try {
    const docRef = doc(db, 'templates', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Template not found');
    }
    
    return convertFirestoreDoc<TaskTemplate>(docSnap);
  } catch (error) {
    console.error('Error getting template:', error);
    throw error;
  }
};

export const createTemplate = async (templateData: Omit<TaskTemplate, 'id'>): Promise<TaskTemplate> => {
  try {
    // Get the highest order value for templates in the same category and add 1
    const existingTemplates = await getTemplates();
    const templatesInCategory = existingTemplates.filter(t => t.category === templateData.category);
    const maxOrder = templatesInCategory.length > 0 ? Math.max(...templatesInCategory.map(t => t.order || 0)) : 0;
    const newOrder = maxOrder + 1;
    
    const firestoreData = convertToFirestore({
      ...templateData,
      order: newOrder
    });
    const docRef = await addDoc(templatesCollection, {
      ...firestoreData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return {
      ...templateData,
      order: newOrder,
      id: docRef.id,
    };
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

export const updateTemplate = async (id: string, updates: Partial<TaskTemplate>): Promise<TaskTemplate> => {
  try {
    const docRef = doc(db, 'templates', id);
    const firestoreData = convertToFirestore(updates);
    
    await updateDoc(docRef, {
      ...firestoreData,
      updatedAt: serverTimestamp(),
    });
    
    const updatedDoc = await getDoc(docRef);
    return convertFirestoreDoc<TaskTemplate>(updatedDoc);
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

export const updateTemplateOrder = async (templateId: string, newOrder: number): Promise<void> => {
  try {
    const docRef = doc(db, 'templates', templateId);
    await updateDoc(docRef, {
      order: newOrder,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating template order:', error);
    throw error;
  }
};

export const deleteTemplate = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'templates', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// Function to apply templates to a new hotel
export const applyTemplatesToHotel = async (hotelId: string): Promise<void> => {
  try {
    console.log(`🔄 Applying templates to hotel: ${hotelId}`);
    
    const templates = await getTemplates();
    console.log(`📋 Found ${templates.length} total templates`);
    
    const activeTemplates = templates.filter(template => template.isActive);
    console.log(`✅ Found ${activeTemplates.length} active templates`);
    
    if (activeTemplates.length === 0) {
      console.log('⚠️ No active templates found to apply');
      return;
    }
    
    // Sort templates by order to maintain the correct sequence
    const sortedTemplates = activeTemplates.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    for (const template of sortedTemplates) {
      console.log(`📝 Creating task from template: ${template.title} (order: ${template.order})`);
      
      const taskData: Omit<Task, 'id'> = {
        hotelId,
        title: template.title,
        description: template.description,
        status: 'not-started',
        priority: template.priority,
        dueDate: '',
        category: template.category,
        order: template.order, // Preserve the template order
        createdAt: new Date().toISOString(), // Will be overwritten by serverTimestamp()
        updatedAt: new Date().toISOString(), // Will be overwritten by serverTimestamp()
        assignedTo: '',
        comments: [],
        files: [],
      };
      
      const createdTask = await createTask(taskData);
      console.log(`✅ Created task: ${createdTask.title} (ID: ${createdTask.id})`);
    }
    
    // Add a small delay to ensure Firestore consistency
    await new Promise(resolve => setTimeout(resolve, 500));
    
    console.log(`🎉 Successfully applied ${activeTemplates.length} templates to hotel ${hotelId}`);
  } catch (error) {
    console.error('❌ Error applying templates to hotel:', error);
    throw error;
  }
};

// Category Collection Functions
export const categoriesCollection = collection(db, 'categories');

export const getCategories = async (): Promise<Category[]> => {
  try {
    const querySnapshot = await getDocs(categoriesCollection);
    const categories = querySnapshot.docs.map(doc => convertFirestoreDoc<Category>(doc));
    
    // Sort by order field
    return categories.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

export const getCategory = async (id: string): Promise<Category> => {
  try {
    const docRef = doc(db, 'categories', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Category not found');
    }
    
    return convertFirestoreDoc<Category>(docSnap);
  } catch (error) {
    console.error('Error getting category:', error);
    throw error;
  }
};

export const createCategory = async (categoryData: Omit<Category, 'id'>): Promise<Category> => {
  try {
    // Get the highest order value and add 1 for the new category
    const existingCategories = await getCategories();
    const maxOrder = existingCategories.length > 0 ? Math.max(...existingCategories.map(c => c.order || 0)) : 0;
    const newOrder = maxOrder + 1;
    
    const firestoreData = convertToFirestore({
      ...categoryData,
      order: newOrder
    });
    const docRef = await addDoc(categoriesCollection, {
      ...firestoreData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return {
      ...categoryData,
      order: newOrder,
      id: docRef.id,
    };
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

export const updateCategory = async (id: string, updates: Partial<Category>): Promise<Category> => {
  try {
    const docRef = doc(db, 'categories', id);
    const firestoreData = convertToFirestore(updates);
    
    await updateDoc(docRef, {
      ...firestoreData,
      updatedAt: serverTimestamp(),
    });
    
    const updatedDoc = await getDoc(docRef);
    return convertFirestoreDoc<Category>(updatedDoc);
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const updateCategoryOrder = async (categoryId: string, newOrder: number): Promise<void> => {
  try {
    logger.log(`🔥 Firestore: Updating category ${categoryId} order to ${newOrder}`);
    const docRef = doc(db, 'categories', categoryId);
    await updateDoc(docRef, {
      order: newOrder,
      updatedAt: serverTimestamp(),
    });
    logger.log(`✅ Firestore: Successfully updated category ${categoryId} order to ${newOrder}`);
  } catch (error) {
    console.error('Error updating category order:', error);
    throw error;
  }
};

// Migration function to add order fields to existing categories and templates
export const migrateOrderFields = async (): Promise<void> => {
  try {
    console.log('🔄 Starting order field migration...');
    
    // Migrate categories
    const categories = await getDocs(categoriesCollection);
    for (let i = 0; i < categories.docs.length; i++) {
      const doc = categories.docs[i];
      const data = doc.data();
      
      if (data.order === undefined) {
        console.log(`📋 Adding order field to category: ${data.name}`);
        await updateDoc(doc.ref, {
          order: i + 1,
          updatedAt: serverTimestamp(),
        });
      }
    }
    
    // Migrate templates
    const templates = await getDocs(templatesCollection);
    const templatesByCategory: { [category: string]: any[] } = {};
    
    // Group templates by category
    templates.docs.forEach(doc => {
      const data = doc.data();
      if (!templatesByCategory[data.category]) {
        templatesByCategory[data.category] = [];
      }
      templatesByCategory[data.category].push({ doc, data });
    });
    
    // Add order to templates within each category
    for (const [category, categoryTemplates] of Object.entries(templatesByCategory)) {
      for (let i = 0; i < categoryTemplates.length; i++) {
        const { doc, data } = categoryTemplates[i];
        
        if (data.order === undefined) {
          console.log(`📋 Adding order field to template: ${data.title} in category ${category}`);
          await updateDoc(doc.ref, {
            order: i + 1,
            updatedAt: serverTimestamp(),
          });
        }
      }
    }
    
    // Migrate tasks (for future use)
    const tasks = await getDocs(collection(db, 'tasks'));
    const tasksByHotelAndCategory: { [key: string]: any[] } = {};
    tasks.docs.forEach(doc => {
      const data = doc.data();
      const key = `${data.hotelId}-${data.category}`;
      if (!tasksByHotelAndCategory[key]) {
        tasksByHotelAndCategory[key] = [];
      }
      tasksByHotelAndCategory[key].push({ doc, data });
    });
    
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [key, hotelCategoryTasks] of Object.entries(tasksByHotelAndCategory)) {
      for (let i = 0; i < hotelCategoryTasks.length; i++) {
        const { doc, data } = hotelCategoryTasks[i];
        if (data.order === undefined) {
          console.log(`📋 Adding order field to task: ${data.title} in hotel ${data.hotelId}, category ${data.category}`);
          await updateDoc(doc.ref, {
            order: i + 1,
            updatedAt: serverTimestamp(),
          });
        }
      }
    }
    
    console.log('✅ Order field migration completed');
  } catch (error) {
    console.error('❌ Error during order field migration:', error);
    throw error;
  }
};

export const deleteCategory = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'categories', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// User Management Functions
export const usersCollection = collection(db, 'users');

export const getUsers = async (): Promise<User[]> => {
  try {
    const querySnapshot = await getDocs(usersCollection);
    const users = querySnapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as User[];
    
    return users.sort((a, b) => a.displayName.localeCompare(b.displayName));
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

export const getUser = async (email: string): Promise<User | null> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const docRef = doc(db, 'users', normalizedEmail);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return {
      ...docSnap.data(),
      id: docSnap.id
    } as User;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> => {
  try {
    const docRef = doc(db, 'users', userData.email);
    
    const newUser = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    await setDoc(docRef, newUser);
    
    return {
      ...newUser,
      id: docRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as User;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (email: string, updates: Partial<User>): Promise<User> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const docRef = doc(db, 'users', normalizedEmail);
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    
    const updatedDoc = await getDoc(docRef);
    return {
      ...updatedDoc.data(),
      id: updatedDoc.id
    } as User;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (email: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const docRef = doc(db, 'users', normalizedEmail);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const deactivateUser = async (email: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const docRef = doc(db, 'users', normalizedEmail);
    await updateDoc(docRef, {
      isActive: false,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

export const activateUser = async (email: string): Promise<void> => {
  try {
    const normalizedEmail = email.toLowerCase();
    const docRef = doc(db, 'users', normalizedEmail);
    await updateDoc(docRef, {
      isActive: true,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error activating user:', error);
    throw error;
  }
};

// Feedback Collection Functions
export const getFeedback = async (): Promise<Feedback[]> => {
  try {
    const querySnapshot = await getDocs(feedbackCollection);
    const feedbacks = querySnapshot.docs.map(doc => convertFirestoreDoc<Feedback>(doc));
    return feedbacks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('Error getting feedback:', error);
    throw error;
  }
};

export const getFeedbackByHotel = async (hotelId: string): Promise<Feedback[]> => {
  try {
    const querySnapshot = await getDocs(query(feedbackCollection, where('hotelId', '==', hotelId)));
    const feedbacks = querySnapshot.docs.map(doc => convertFirestoreDoc<Feedback>(doc));
    return feedbacks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('Error getting feedback by hotel:', error);
    throw error;
  }
};

export const addFeedback = async (feedbackData: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt'>): Promise<Feedback> => {
  try {
    // Filter out undefined values to prevent Firestore errors
    const cleanFeedbackData = Object.fromEntries(
      Object.entries(feedbackData).filter(([_, value]) => value !== undefined)
    );
    
    const docRef = await addDoc(feedbackCollection, {
      ...cleanFeedbackData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return {
      ...feedbackData,
      id: docRef.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error adding feedback:', error);
    throw error;
  }
};

export const updateFeedback = async (id: string, updates: Partial<Feedback>): Promise<void> => {
  try {
    const docRef = doc(feedbackCollection, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating feedback:', error);
    throw error;
  }
};

export const deleteFeedback = async (id: string): Promise<void> => {
  try {
    const docRef = doc(feedbackCollection, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting feedback:', error);
    throw error;
  }
};

export const subscribeToFeedback = (callback: (feedbacks: Feedback[]) => void) => {
  return onSnapshot(feedbackCollection, (querySnapshot) => {
    try {
      const feedbacks = querySnapshot.docs.map(doc => convertFirestoreDoc<Feedback>(doc));
      callback(feedbacks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      logger.error('Error in feedback listener:', error);
      callback([]);
    }
  });
};

export const subscribeToFeedbackByHotel = (hotelId: string, callback: (feedbacks: Feedback[]) => void) => {
  return onSnapshot(query(feedbackCollection, where('hotelId', '==', hotelId)), (querySnapshot) => {
    try {
      const feedbacks = querySnapshot.docs.map(doc => convertFirestoreDoc<Feedback>(doc));
      callback(feedbacks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      logger.error('Error in feedback by hotel listener:', error);
      callback([]);
    }
  });
};

export const addFeedbackComment = async (feedbackId: string, comment: Omit<FeedbackComment, 'id' | 'timestamp'>): Promise<FeedbackComment> => {
  try {
    const commentsCollection = collection(db, 'feedback', feedbackId, 'comments');
    const docRef = await addDoc(commentsCollection, {
      ...comment,
      timestamp: serverTimestamp(),
    });
    return {
      ...comment,
      id: docRef.id,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error adding feedback comment:', error);
    throw error;
  }
};

export const deleteFeedbackComment = async (feedbackId: string, commentId: string): Promise<void> => {
  try {
    const commentRef = doc(db, 'feedback', feedbackId, 'comments', commentId);
    await deleteDoc(commentRef);
  } catch (error) {
    console.error('Error deleting feedback comment:', error);
    throw error;
  }
};

export const subscribeToFeedbackComments = (feedbackId: string, callback: (comments: FeedbackComment[]) => void) => {
  const commentsCollection = collection(db, 'feedback', feedbackId, 'comments');
  return onSnapshot(commentsCollection, (querySnapshot) => {
    try {
      const comments = querySnapshot.docs.map(commentDoc => convertFirestoreDoc<FeedbackComment>(commentDoc));
      callback(comments.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()));
    } catch (error) {
      logger.error('Error in feedback comments listener:', error);
      callback([]);
    }
  });
};
